import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon, BarChart3Icon, CheckCircle2Icon, LandmarkIcon, ShieldCheckIcon, UsersIcon } from 'lucide-react';
import { Language } from '../App';
import { SectionHeading } from '../components/SectionHeading';
export function HomePage({
  language
}: {
  language: Language;
}) {
  const ar = language === 'ar';
  const strengths = [[LandmarkIcon, ar ? 'حلول متكاملة' : 'Integrated solutions', ar ? 'تمويل منظم لكل مرحلة من مراحل النمو.' : 'Structured finance for every stage of growth.'], [ShieldCheckIcon, ar ? 'حوكمة راسخة' : 'Disciplined governance', ar ? 'معايير دقيقة للامتثال والسرية.' : 'Rigorous standards for compliance and confidentiality.'], [UsersIcon, ar ? 'شبكة عالمية' : 'Global reach', ar ? 'رأس مال خاص ومؤسسي لفرص مؤهلة.' : 'Private and institutional capital for qualified opportunities.']];
  const steps = ar ? ['تقديم الطلب', 'تقييم أولي', 'هيكلة الحل', 'العناية الواجبة', 'الموافقة', 'التمويل والدعم'] : ['Application', 'Initial review', 'Solution structuring', 'Due diligence', 'Approval', 'Funding & support'];
  return <>
      <section className="relative isolate min-h-[650px] overflow-hidden bg-[#061d34]" data-id="element-166">
        <img src="https://images.unsplash.com/photo-1586724237569-f3d0c1dee8c6?auto=format&fit=crop&w=2000&q=85" alt="Riyadh skyline at dusk" className="absolute inset-0 h-full w-full object-cover opacity-45" data-id="element-167" />
        <div className="absolute inset-0 bg-[#061d34]/65" data-id="element-168" />
        <div className="relative mx-auto flex min-h-[650px] max-w-7xl items-center px-5 py-24 lg:px-8" data-id="element-169">
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.65
        }} className="max-w-3xl" data-id="element-170">
            <p className="mb-6 text-xs font-bold uppercase tracking-[0.28em] text-[#e0bf70]" data-id="element-171">
              {ar ? 'شريكك المالي الموثوق' : 'Your trusted financial partner'}
            </p>
            <h1 className="font-serif text-5xl leading-[1.05] text-white sm:text-6xl lg:text-7xl" data-id="element-172">
              {ar ? 'قروض وتمويل للنمو. وتمكين للنجاح.' : 'Loans & Finance for Growth. Empowering Success.'}
            </h1>
            <p className="mt-7 max-w-2xl text-lg leading-8 text-slate-200" data-id="element-173">
              {ar ? 'نقدم القروض التجارية وحلول التمويل الاستراتيجية للشركات والمشاريع الطموحة في المملكة وخارجها.' : 'United Third Financial Company provides commercial loans and strategic financing for ambitious businesses and projects across the Kingdom and beyond.'}
            </p>
            <div className="mt-9 flex flex-wrap gap-4" data-id="element-174">
              <Link to="/contact" className="inline-flex items-center gap-2 bg-[#bf9744] px-6 py-3.5 text-sm font-bold text-white transition-colors hover:bg-[#a87e2f]" data-id="element-175">
                {ar ? 'تقدم بطلب قرض أو تمويل' : 'Apply for a Loan or Funding'}{' '}
                <ArrowRightIcon size={16} className="rtl:rotate-180" data-id="element-176" />
              </Link>
              <Link to="/contact" className="inline-flex items-center gap-2 border border-white/60 px-6 py-3.5 text-sm font-bold text-white transition-colors hover:bg-white hover:text-[#08233f]" data-id="element-177">
                {ar ? 'اتصل بنا' : 'Contact Us'}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="bg-white py-20" data-id="element-178">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 lg:grid-cols-2 lg:px-8" data-id="element-179">
          <SectionHeading language={language} eyebrow={ar ? 'لمحة عامة' : 'Institutional overview'} title={ar ? 'قروض موثوقة ورأس مال هادف للأعمال الجادة.' : 'Trusted loans and purposeful capital for serious enterprise.'} text={ar ? 'نوفر القروض التجارية أولاً، إلى جانب تمويل الشركات والمشاريع والعقود والاستثمارات والتجارة والبنية التحتية والتوسع، بمنهج واضح واحترافي.' : 'We provide commercial loans first, alongside corporate and project finance, contract finance, investment solutions, trade finance, infrastructure funding, and expansion capital through a clear, professional approach.'} data-id="element-180" />
          <div className="grid gap-5 sm:grid-cols-3" data-id="element-181">
            {strengths.map(([Icon, title, text]) => <div key={title as string} className="border-l-2 border-[#bf9744] bg-[#f6f7f8] p-5 rtl:border-l-0 rtl:border-r-2" data-id="element-182">
                <Icon className="mb-5 text-[#a47b2a]" size={25} data-id="element-183" />
                <h3 className="font-serif text-lg" data-id="element-184">{title as string}</h3>
                <p className="mt-2 text-sm leading-6 text-slate-600" data-id="element-185">
                  {text as string}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      <section className="bg-[#f1f3f5] py-20" data-id="element-186">
        <div className="mx-auto max-w-7xl px-5 lg:px-8" data-id="element-187">
          <SectionHeading language={language} centered eyebrow={ar ? 'منهجنا' : 'Our process'} title={ar ? 'مسار واضح من الطموح إلى التنفيذ.' : 'A clear path from ambition to execution.'} text={ar ? 'نقود كل فرصة بالانضباط والشفافية وسرعة الاستجابة.' : 'We guide every opportunity with discipline, transparency, and responsiveness.'} data-id="element-188" />
          <ol className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-6" data-id="element-189">
            {steps.map((step, i) => <li key={step} className="relative bg-white p-5 shadow-sm" data-id="element-190">
                <span className="font-serif text-3xl text-[#c49d4f]" data-id="element-191">
                  0{i + 1}
                </span>
                <p className="mt-8 text-sm font-bold text-[#08233f]" data-id="element-192">{step}</p>
              </li>)}
          </ol>
        </div>
      </section>

      <section className="bg-[#08233f] py-18" data-id="element-193">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-8 px-5 py-16 md:flex-row md:items-center lg:px-8" data-id="element-194">
          <div data-id="element-195">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#d6b45f]" data-id="element-196">
              {ar ? 'لنبدأ' : 'Begin the conversation'}
            </p>
            <h2 className="mt-3 font-serif text-3xl text-white" data-id="element-197">
              {ar ? 'تمويل مصمم وفقاً لطموحك.' : 'Finance designed around your ambition.'}
            </h2>
          </div>
          <Link to="/services" className="inline-flex items-center gap-2 border border-[#d6b45f] px-5 py-3 text-sm font-bold text-[#e4c779] hover:bg-[#d6b45f] hover:text-[#08233f]" data-id="element-198">
            {ar ? 'استكشف الحلول' : 'Explore solutions'}{' '}
            <BarChart3Icon size={16} data-id="element-199" />
          </Link>
        </div>
      </section>
    </>;
}